import Colors from '@/Colors';
import DynamicGeofencesContourType from '@/common/components/controls/dynamic-geofences/enums/DynamicGeofencesContourType.enum';
import DynamicGeofencesEvent from '@/common/components/controls/dynamic-geofences/enums/DynamicGeofencesEvent.enum';
import DynamicGeofencesProfile from '@/common/components/controls/dynamic-geofences/enums/DynamicGeofencesProfile.enum';
import DynamicGeofenceUnit from '@/common/components/controls/dynamic-geofences/enums/DynamicGeofenceUnit.enum';
import Cycling from '@/common/components/controls/dynamic-geofences/icons/Cycling.vue';
import Driving from '@/common/components/controls/dynamic-geofences/icons/Driving.vue';
import Walking from '@/common/components/controls/dynamic-geofences/icons/Walking.vue';
import ContourMinMaxValue from '@/common/components/controls/dynamic-geofences/models/ContourMinMaxValue.model';
import Geofence from '@/common/models/geo/Geofence';
import GlobalEventBus from '@/GlobalEventBus';

interface DynamicGeofencesOption {
  name: string;
  selected: boolean;
  icon: unknown;
}

export interface DynamicGeofencesProfileOption extends DynamicGeofencesOption {
  profile: DynamicGeofencesProfile;
}

export interface DynamicGeofencesContourOption extends DynamicGeofencesOption {
  contour: DynamicGeofencesContourType;
  unit: DynamicGeofenceUnit;
  unitName: string;
}

const defaultDynamicGeofencesProfileOptions: DynamicGeofencesProfileOption[] = [
  {
    profile: DynamicGeofencesProfile.Driving,
    name: 'messages.driving',
    selected: true,
    icon: Driving,
  },
  {
    profile: DynamicGeofencesProfile.Walking,
    name: 'messages.walking',
    selected: false,
    icon: Walking,
  },
  {
    profile: DynamicGeofencesProfile.Cycling,
    name: 'messages.cycling',
    selected: false,
    icon: Cycling,
  },
];

const defaultDynamicGeofencesContourOptions: DynamicGeofencesContourOption[] = [
  {
    contour: DynamicGeofencesContourType.Time,
    name: 'messages.time',
    selected: true,
    icon: Driving,
    unit: DynamicGeofenceUnit.Minutes,
    unitName: 'names.minute',
  },
  {
    contour: DynamicGeofencesContourType.Distance,
    name: 'messages.distance',
    selected: false,
    icon: Driving,
    unit: DynamicGeofenceUnit.Meters,
    unitName: 'options.distanceUnits.meters',
  },
];

const contourMinMaxValues: { [key: string]: ContourMinMaxValue } = {
  [DynamicGeofencesContourType.Distance]: {
    min: 1,
    max: 100000,
    unit: DynamicGeofenceUnit.Meters,
  },
  [DynamicGeofencesContourType.Time]: {
    min: 1,
    max: 60,
    unit: DynamicGeofenceUnit.Minutes,
  },
};

function getMapLayerId(geofenceId: number) {
  return `${geofenceId}_isochrone`;
}

function removeLayer(map: mapboxgl.Map, layerId: string): void {
  map.removeLayer(layerId);
  map.removeSource(layerId);
}

function drawLayer(map: mapboxgl.Map, layerId: string, geofence: Geofence): void {
  const { dynamicGeofences, id } = geofence;
  const { featureCollection } = dynamicGeofences;
  map.addSource(layerId, { type: 'geojson', data: featureCollection });

  map.addLayer({
    id: layerId,
    type: 'fill',
    source: layerId,
    layout: {},
    paint: { 'fill-color': Colors.geofences, 'fill-opacity': 0.3 },
  });

  map.on('click', layerId, () => {
    GlobalEventBus.$emit(DynamicGeofencesEvent.POLYGON_CLICKED, id, false, false);
  });
}

function drawOnTheMap(map: mapboxgl.Map, geofence: Geofence) {
  const { id } = geofence;
  const layerId = getMapLayerId(id);

  if (map.getSource(layerId)) {
    removeLayer(map, layerId);
  }

  drawLayer(map, layerId, geofence);
}

const DynamicGeofencesUtils = {
  contourMinMaxValues,
  defaultDynamicGeofencesProfileOptions,
  defaultDynamicGeofencesContourOptions,

  drawOnTheMap,
  getMapLayerId,
  removeLayer,
};

export default DynamicGeofencesUtils;
